import * as React from 'react';
import {
  Check,
  ChevronDown,
  Loader2,
  Lock,
  Plug,
  Plus,
  Trash2,
} from 'lucide-react';
import {
  CUSTOM_PARSERS,
  connectionKind,
  findCustomParser,
  isPixxiType,
  type CrmConnection,
  type CrmConnections,
  type LeadRouting,
} from '@proptr/shared';
import { useAuth } from '@proptr/api-client/react';
import { AppShell } from '../components/layout/AppShell.js';
import { Button } from '../components/ui/button.js';
import { Card } from '../components/ui/card.js';
import { Input } from '../components/ui/input.js';
import { Label } from '../components/ui/label.js';
import { Select, type SelectOption } from '../components/ui/select.js';
import { Switch } from '../components/ui/switch.js';
import { cn } from '../lib/utils.js';
import { client } from '../main.js';
import { Link } from 'react-router-dom';
import type { CrmSyncStatus } from '@proptr/api-client';

export function CrmSettingsPage() {
  const { enterprise, refresh } = useAuth();
  // Master switch — stored on the enterprise (agency) doc. Missing =
  // enabled, so legacy brokerages behave as before.
  const [syncEnabled, setSyncEnabled] = React.useState(
    enterprise?.crmSyncEnabled !== false,
  );
  const [syncSaving, setSyncSaving] = React.useState(false);
  const [syncError, setSyncError] = React.useState<string | null>(null);
  // Whether at least one CRM connection is saved — gates the "Send
  // leads to CRM" toggle. null until the connections section loads.
  const [hasCrm, setHasCrm] = React.useState<boolean | null>(null);
  // Bumped after a connections save so lead routing refetches (the API
  // auto-flips leadRouting.toCrm when connections change).
  const [routingRefresh, setRoutingRefresh] = React.useState(0);

  const toggleSync = async (next: boolean) => {
    if (syncSaving) return;
    setSyncEnabled(next); // optimistic — the sections gray out instantly
    setSyncSaving(true);
    setSyncError(null);
    try {
      await client.enterprise.updateCrmSync(next);
      // Refresh the session so the topbar tabs (Agents/Listings/Proptree)
      // appear or disappear without a reload.
      await refresh();
    } catch (err) {
      setSyncEnabled(!next);
      setSyncError((err as Error).message || 'Could not save — try again.');
    } finally {
      setSyncSaving(false);
    }
  };

  return (
    <AppShell>
      <div className="space-y-6 pb-10">
        <div>
          <h1 className="font-display text-[40px] font-medium leading-none tracking-[-0.02em]">
            CRM settings
          </h1>
          <p className="mt-3 font-mono text-[11px] text-muted-foreground">
            Connect your CRM and choose how captured leads are routed.
          </p>
        </div>

        <CrmSyncSection
          enabled={syncEnabled}
          saving={syncSaving}
          error={syncError}
          onToggle={toggleSync}
        />

        {/* Grayed out (not hidden) while CRM sync is off — settings are
            kept, just not editable. */}
        <div
          aria-disabled={!syncEnabled}
          className={cn(
            'grid grid-cols-1 items-start gap-6 lg:grid-cols-2',
            !syncEnabled && 'pointer-events-none select-none opacity-50',
          )}
        >
          <CrmConnectionsSection
            onStatusChange={(has) => setHasCrm(has)}
            onSaved={() => setRoutingRefresh((n) => n + 1)}
          />
          <LeadManagementSection
            hasCrm={hasCrm}
            refreshKey={routingRefresh}
          />
        </div>
      </div>
    </AppShell>
  );
}

// Master switch card asking whether the brokerage syncs a CRM at all.
// Off: the sections below gray out and the Agents/Listings/Proptree
// tabs hide in the main menu. Nothing is deleted either way.
function CrmSyncSection({
  enabled,
  saving,
  error,
  onToggle,
}: {
  enabled: boolean;
  saving: boolean;
  error: string | null;
  onToggle: (next: boolean) => void;
}) {
  return (
    <Card className="flex items-center gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="font-sans text-[13px] font-semibold leading-tight text-foreground">
          Sync a CRM with Proptree
        </div>
        <p
          className={cn(
            'mt-1 font-sans text-[11px] leading-snug',
            error ? 'text-destructive' : 'text-muted-foreground',
          )}
        >
          {error ??
            (enabled
              ? 'Listings and agents sync from your CRM. Turning this off grays out CRM settings and hides the Agents, Listings, and Proptree tabs — nothing is deleted.'
              : 'CRM sync is off — CRM connection and lead management are disabled, and the Agents, Listings, and Proptree tabs are hidden. Turn it back on anytime; everything is kept.')}
        </p>
      </div>
      {saving && (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      )}
      <Switch
        checked={enabled}
        onCheckedChange={onToggle}
        disabled={saving}
        aria-label="Sync a CRM with Proptree"
      />
    </Card>
  );
}

// ====================================================================
// CRM connections
// ====================================================================

// The CRMs the sync supports: Pixxi, Property Finder, PropSpace and
// PropData over their APIs (PropSpace also by XML feed), Propfusion and
// Goyzer by XML feed — each with its own way in for leads — and a Google
// Sheet on the listing template, for units or projects.
// The rest are visible but not selectable yet. Existing saved connections
// of a disabled type keep working — only NEW selection is blocked.
// Pixxi is two feeds, named as in legacy: units (ready + off-plan resale
// units) and projects (off-plan developments, one listing per project).
const CRM_OPTIONS: SelectOption[] = [
  { value: 'pixxi', label: 'Pixxi CRM – Units' },
  { value: 'pixxi_project', label: 'Pixxi CRM – Projects (off-plan)' },
  { value: 'propertyfinder', label: 'Property Finder' },
  { value: 'propspace', label: 'PropSpace CRM' },
  { value: 'propfusion', label: 'Propfusion CRM' },
  { value: 'goyzer', label: 'Goyzer CRM' },
  { value: 'propdata', label: 'PropData', badge: 'Beta' },
  { value: 'googlesheet', label: 'Google Sheet' },
  { value: 'other', label: 'Something else', disabled: true, badge: 'Coming soon' },
];

// The CRM dropdown: the shared CRMs plus the brokerage-specific parsers
// admin enabled for this agency (ported ones only). A connection on a
// parser that's since been disabled still shows, so it can be removed.
function useCrmOptions(conn: CrmConnection): SelectOption[] {
  const { enterprise } = useAuth();
  const allowed = new Set(enterprise?.customParsers ?? []);
  const custom: SelectOption[] = CUSTOM_PARSERS.filter((p) => p.ready && allowed.has(p.key)).map(
    (p) => ({ value: `custom:${p.key}`, label: p.label }),
  );
  if (conn.type === 'custom' && conn.customParser && !custom.some((o) => o.value === `custom:${conn.customParser}`)) {
    custom.push({
      value: `custom:${conn.customParser}`,
      label: findCustomParser(conn.customParser)?.label ?? conn.customParser,
      disabled: true,
      badge: 'Not enabled',
    });
  }
  return [...CRM_OPTIONS, ...custom];
}

// What a connection supplies. A brokerage running both ("mixed") adds
// one connection of each — e.g. PropSpace units + Pixxi projects.
const KIND_OPTIONS: SelectOption[] = [
  { value: 'units', label: 'Units (ready & off-plan)' },
  { value: 'projects', label: 'Projects (off-plan developments)' },
];

// CRMs that authenticate with apiKey/apiSecret instead of a feed URL.
const KEY_AUTH = new Set<CrmConnection['type']>(['propertyfinder']);
// CRMs that authenticate with a single API token (Pixxi: X-PIXXI-TOKEN).
const TOKEN_AUTH = new Set<CrmConnection['type']>(['pixxi', 'pixxi_project']);

function newId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `c-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function blankConnection(): CrmConnection {
  return { id: newId(), label: 'both', type: 'pixxi', apiToken: '' };
}

function CrmConnectionsSection({
  onStatusChange,
  onSaved,
}: {
  onStatusChange: (hasConnections: boolean) => void;
  onSaved: () => void;
}) {
  const [items, setItems] = React.useState<CrmConnection[] | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  // CRM sync is a paid feature — `null` while unknown so the controls
  // don't flash as locked before the plan lands.
  const [paidPlan, setPaidPlan] = React.useState<boolean | null>(null);
  // Latest pull per connection id — counts + why listings were held back.
  const [syncStatus, setSyncStatus] = React.useState<Record<
    string,
    CrmSyncStatus
  > | null>(null);

  React.useEffect(() => {
    let alive = true;
    client.billing
      .summary({ lite: true })
      .then((b) => {
        if (alive) setPaidPlan(b.plan !== 'trial');
      })
      .catch(() => {
        // Unknown plan — don't lock the UI on a failed lookup; the
        // server still rejects a save without a plan.
        if (alive) setPaidPlan(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  React.useEffect(() => {
    let alive = true;
    client.enterprise
      .getCrmSyncStatus()
      .then((res) => {
        if (alive) setSyncStatus(res);
      })
      .catch(() => {
        if (alive) setSyncStatus({});
      });
    return () => {
      alive = false;
    };
  }, []);

  React.useEffect(() => {
    let alive = true;
    client.enterprise
      .getCrmConnections()
      .then((res) => {
        if (!alive) return;
        setItems(res);
        onStatusChange(res.length > 0);
        setLoading(false);
      })
      .catch((err: Error) => {
        if (!alive) return;
        setLoadError(err.message || "Couldn't load your CRM connections.");
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const update = (id: string, patch: Partial<CrmConnection>) => {
    setSaved(false);
    setItems((prev) =>
      prev ? prev.map((c) => (c.id === id ? { ...c, ...patch } : c)) : prev,
    );
  };
  const remove = (id: string) => {
    setSaved(false);
    setItems((prev) => (prev ? prev.filter((c) => c.id !== id) : prev));
  };
  const add = () => {
    setSaved(false);
    setItems((prev) => [...(prev ?? []), blankConnection()]);
  };

  async function handleSave() {
    if (!items || saving) return;
    setError(null);

    // Validate + strip credentials irrelevant to each CRM type.
    const cleaned: CrmConnections = [];
    for (let i = 0; i < items.length; i++) {
      const c = items[i]!;
      const n = i + 1;
      const priceFilter = cleanPriceFilter(c.priceFilter);
      if (priceFilter === 'invalid') {
        setError(`Connection ${n}: a minimum price is above its maximum.`);
        return;
      }
      const base = {
        id: c.id,
        label: c.label,
        type: c.type,
        kind: connectionKind(c),
        ...(priceFilter ? { priceFilter } : {}),
      };
      if (isPixxiType(c.type)) {
        if (!c.sourceUrl?.trim()) {
          setError(`Connection ${n}: enter your company name in Pixxi.`);
          return;
        }
        if (!c.apiToken?.trim()) {
          setError(`Connection ${n}: paste your Pixxi API token.`);
          return;
        }
        cleaned.push({
          ...base,
          sourceUrl: c.sourceUrl.trim(),
          apiToken: c.apiToken.trim(),
        });
      } else if (c.type === 'propertyfinder') {
        if (!c.apiKey?.trim() || !c.apiSecret?.trim()) {
          setError(`Connection ${n}: enter the API key and secret.`);
          return;
        }
        cleaned.push({
          ...base,
          apiKey: c.apiKey.trim(),
          apiSecret: c.apiSecret.trim(),
        });
      } else if (c.type === 'propspace') {
        // The API login (listings, agents and leads) or the XML feed — or
        // both, the feed then only adding amenities.
        const id = c.apiKey?.trim() ?? '';
        const secret = c.apiSecret?.trim() ?? '';
        const feed = c.sourceUrl?.trim() ?? '';
        if (Boolean(id) !== Boolean(secret)) {
          setError(`Connection ${n}: enter both the PropSpace client ID and secret, or neither.`);
          return;
        }
        if (!id && !feed) {
          setError(`Connection ${n}: enter the PropSpace API client ID and secret, or a feed URL.`);
          return;
        }
        cleaned.push({
          ...base,
          ...(feed ? { sourceUrl: feed } : {}),
          ...(id ? { apiKey: id, apiSecret: secret } : {}),
        });
      } else if (c.type === 'propdata') {
        const p = c.propdata ?? {};
        if (!p.username?.trim() || !p.password) {
          setError(`Connection ${n}: enter the PropData API username and password.`);
          return;
        }
        cleaned.push({
          ...base,
          propdata: {
            username: p.username.trim(),
            password: p.password,
            siteId: p.siteId?.trim() || undefined,
            branchId: p.branchId?.trim() || undefined,
            environment: p.environment === 'staging' ? 'staging' : undefined,
          },
        });
      } else if (c.type === 'propfusion') {
        if (!c.sourceUrl?.trim()) {
          setError(`Connection ${n}: enter the feed URL.`);
          return;
        }
        const leadUrl = c.leadUrl?.trim() ?? '';
        if (leadUrl && !/^https?:\/\//i.test(leadUrl)) {
          setError(`Connection ${n}: the lead webhook must be an http(s) URL.`);
          return;
        }
        cleaned.push({
          ...base,
          sourceUrl: c.sourceUrl.trim(),
          ...(leadUrl ? { leadUrl } : {}),
        });
      } else if (c.type === 'goyzer') {
        if (!c.sourceUrl?.trim()) {
          setError(`Connection ${n}: enter the feed URL.`);
          return;
        }
        // Lead push settings — optional, but the access and group code
        // only work together.
        const g = c.goyzer ?? {};
        const accessCode = g.accessCode?.trim() ?? '';
        const groupCode = g.groupCode?.trim() ?? '';
        if (Boolean(accessCode) !== Boolean(groupCode)) {
          setError(`Connection ${n}: enter both the Goyzer access code and group code, or neither.`);
          return;
        }
        const fallbackBrokerId = g.fallbackBrokerId?.trim() || undefined;
        if (g.alwaysFallback && !fallbackBrokerId) {
          setError(`Connection ${n}: enter the fallback agent ID to send every lead to.`);
          return;
        }
        const extras = {
          mediaName: g.mediaName?.trim() || undefined,
          methodOfContact: g.methodOfContact?.trim() || undefined,
          fallbackBrokerId,
          ...(g.alwaysFallback ? { alwaysFallback: true } : {}),
        };
        // Kept without the codes too, so routing set up ahead of them
        // (e.g. seeded from legacy) survives a save.
        const hasSettings = Boolean(accessCode) || Object.values(extras).some(Boolean);
        cleaned.push({
          ...base,
          sourceUrl: c.sourceUrl.trim(),
          ...(hasSettings
            ? { goyzer: { ...(accessCode ? { accessCode, groupCode } : {}), ...extras } }
            : {}),
        });
      } else if (c.type === 'custom') {
        if (!c.sourceUrl?.trim()) {
          setError(`Connection ${n}: enter the feed URL.`);
          return;
        }
        cleaned.push({ ...base, customParser: c.customParser, sourceUrl: c.sourceUrl.trim() });
      } else if (c.type === 'other') {
        if (!c.name?.trim()) {
          setError(`Connection ${n}: tell us which CRM you use.`);
          return;
        }
        if (!c.sourceUrl?.trim()) {
          setError(`Connection ${n}: enter the feed URL.`);
          return;
        }
        cleaned.push({
          ...base,
          name: c.name.trim(),
          sourceUrl: c.sourceUrl.trim(),
        });
      } else {
        if (!c.sourceUrl?.trim()) {
          setError(`Connection ${n}: enter the feed URL.`);
          return;
        }
        cleaned.push({ ...base, sourceUrl: c.sourceUrl.trim() });
      }
    }

    setSaving(true);
    try {
      const next = await client.enterprise.updateCrmConnections(cleaned);
      setItems(next);
      onStatusChange(next.length > 0);
      onSaved();
      setSaved(true);
    } catch (err) {
      setError((err as Error).message || 'Could not save. Try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="p-6">
      <div className="font-sans text-[15px] font-semibold text-foreground">
        CRM connection
      </div>
      <p className="mt-1 font-mono text-[11px] text-muted-foreground">
        The CRM feeds your listings sync from. Add one per market segment.
      </p>

      {loading ? (
        <div className="mt-5 space-y-4">
          {[0, 1].map((i) => (
            <div key={i} className="space-y-2">
              <div className="h-3 w-24 animate-pulse rounded bg-muted" />
              <div className="h-11 w-full animate-pulse rounded-md bg-muted" />
            </div>
          ))}
        </div>
      ) : loadError ? (
        <div className="mt-5 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 font-mono text-[12px] text-destructive">
          {loadError}
        </div>
      ) : items ? (
        <>
          {paidPlan === false && (
            <div className="mt-4 flex flex-wrap items-center gap-3 rounded-md border border-warning/50 bg-warning/10 px-4 py-3">
              <Lock className="h-3.5 w-3.5 shrink-0 text-warning" />
              <span className="min-w-0 flex-1 font-sans text-[12px] leading-snug text-foreground">
                CRM sync is part of a paid plan. Choose a plan to connect
                your CRM and start syncing listings and agents.
              </span>
              <Button asChild size="sm" variant="outline" className="shrink-0">
                <Link to="/billing">Choose a plan</Link>
              </Button>
            </div>
          )}
          <div className="mt-4 space-y-4">
            {items.length === 0 && (
              <div className="rounded-lg border border-dashed border-border p-6 text-center font-mono text-[11px] text-muted-foreground">
                No CRM connected yet. Add one to start syncing listings.
              </div>
            )}
            {items.map((conn, i) => (
              <ConnectionBlock
                key={conn.id}
                conn={conn}
                index={i}
                canRemove={items.length > 1}
                status={syncStatus?.[conn.id] ?? null}
                onChange={(patch) => update(conn.id, patch)}
                onRemove={() => remove(conn.id)}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={add}
            disabled={paidPlan === false}
            className="mt-4 inline-flex items-center gap-1.5 rounded-md border border-dashed border-border px-3 py-2 font-mono text-[11px] font-medium text-muted-foreground transition-colors hover:border-foreground/30 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-border disabled:hover:text-muted-foreground"
          >
            {paidPlan === false ? (
              <Lock className="h-3.5 w-3.5" />
            ) : (
              <Plus className="h-3.5 w-3.5" />
            )}
            Add connection
          </button>

          {error && (
            <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 font-mono text-[12px] text-destructive">
              {error}
            </div>
          )}

          <div className="mt-5 flex items-center justify-end gap-3">
            {saved && !saving && (
              <span className="inline-flex items-center gap-1.5 font-mono text-[11px] text-success">
                <Check className="h-3.5 w-3.5" />
                Saved
              </span>
            )}
            <Button
              onClick={handleSave}
              disabled={saving || paidPlan === false}
            >
              {saving ? (
                <Loader2 className="animate-spin" />
              ) : paidPlan === false ? (
                <Lock />
              ) : (
                <Plug />
              )}
              {saving ? 'Saving…' : 'Save CRM'}
            </Button>
          </div>
        </>
      ) : null}
    </Card>
  );
}

function ConnectionBlock({
  conn,
  index,
  canRemove,
  status,
  onChange,
  onRemove,
}: {
  conn: CrmConnection;
  index: number;
  canRemove: boolean;
  status: CrmSyncStatus | null;
  onChange: (patch: Partial<CrmConnection>) => void;
  onRemove: () => void;
}) {
  const usesKeyAuth = KEY_AUTH.has(conn.type);
  const usesTokenAuth = TOKEN_AUTH.has(conn.type);
  const crmOptions = useCrmOptions(conn);
  return (
    <div className="rounded-lg border border-border p-4 dark:border-white/10">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          {index === 0 ? 'Default connection' : `Connection ${index + 1}`}
        </div>
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            aria-label="Remove connection"
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>Listings</Label>
          <Select
            searchable={false}
            options={KIND_OPTIONS}
            value={connectionKind(conn)}
            // Pixxi's units and projects are separate CRM options.
            disabled={isPixxiType(conn.type)}
            onChange={(v) => onChange({ kind: v === 'projects' ? 'projects' : 'units' })}
          />
        </div>
        <div className="space-y-1.5">
          <Label>CRM</Label>
          <Select
            searchable={false}
            placeholder="Select your CRM…"
            options={crmOptions}
            value={conn.type === 'custom' ? `custom:${conn.customParser ?? ''}` : conn.type}
            onChange={(v) => {
              if (v.startsWith('custom:')) {
                onChange({ type: 'custom', customParser: v.slice('custom:'.length) });
                return;
              }
              const type = v as CrmConnection['type'];
              // Pixxi's two feeds fix the kind; other CRMs keep the choice.
              onChange(
                type === 'pixxi_project'
                  ? { type, label: 'off_plan', kind: 'projects' }
                  : type === 'pixxi'
                    ? { type, kind: 'units' }
                    : { type },
              );
            }}
          />
        </div>
      </div>

      {conn.type === 'other' && (
        <div className="mt-3 space-y-1.5">
          <Label>Which CRM?</Label>
          <Input
            value={conn.name ?? ''}
            onChange={(e) => onChange({ name: e.target.value })}
            placeholder="e.g. Salesforce, HubSpot, in-house tool"
          />
        </div>
      )}

      {usesTokenAuth ? (
        <div className="mt-3 space-y-3">
          <div className="space-y-1.5">
            <Label>Company name in Pixxi</Label>
            <Input
              autoComplete="off"
              spellCheck={false}
              value={conn.sourceUrl ?? ''}
              onChange={(e) => onChange({ sourceUrl: e.target.value })}
              placeholder="Your company name in Pixxi"
            />
          </div>
          <div className="space-y-1.5">
            <Label>API token</Label>
            <Input
              type="password"
              autoComplete="new-password"
              spellCheck={false}
              value={conn.apiToken ?? ''}
              onChange={(e) => onChange({ apiToken: e.target.value })}
              placeholder="X-PIXXI-TOKEN"
            />
            <p className="font-mono text-[10px] text-muted-foreground">
              Pixxi CRM → Admin → Integrations → Pixxi Forms. We use it to
              sync listings and agents, and to push your leads into Pixxi.
            </p>
          </div>
        </div>
      ) : conn.type === 'propdata' ? (
        <PropDataFields
          value={conn.propdata ?? {}}
          onChange={(propdata) => onChange({ propdata })}
        />
      ) : usesKeyAuth ? (
        <div className="mt-3 space-y-3">
          <div className="space-y-1.5">
            <Label>API key</Label>
            <Input
              autoComplete="off"
              spellCheck={false}
              value={conn.apiKey ?? ''}
              onChange={(e) => onChange({ apiKey: e.target.value })}
              placeholder="Issued from your Property Finder Atlas dashboard"
            />
          </div>
          <div className="space-y-1.5">
            <Label>API secret</Label>
            <Input
              type="password"
              autoComplete="new-password"
              spellCheck={false}
              value={conn.apiSecret ?? ''}
              onChange={(e) => onChange({ apiSecret: e.target.value })}
              placeholder="Paired with your API key"
            />
          </div>
        </div>
      ) : (
        <div className="mt-3 space-y-1.5">
          <Label>
            {conn.type === 'propspace'
              ? 'Feed URL (optional with an API login)'
              : conn.type === 'googlesheet'
                ? 'Sheet link'
                : 'Source URL'}
          </Label>
          <Input
            type="url"
            value={conn.sourceUrl ?? ''}
            onChange={(e) => onChange({ sourceUrl: e.target.value })}
            placeholder={
              conn.type === 'googlesheet'
                ? 'https://docs.google.com/spreadsheets/d/…'
                : 'https://your-crm.com/feed.xml'
            }
          />
          <p className="font-mono text-[10px] text-muted-foreground">
            {conn.type === 'googlesheet'
              ? 'A sheet on the Proptree listing template, shared or published to the web (File → Share → Publish to web → CSV). Set Listings to Units or Projects to match the template; only rows with STATUS "enabled" are published.'
              : 'Usually an XML feed. Paste the link your CRM gave you.'}
          </p>
          {conn.type === 'propspace' && (
            <div className="grid grid-cols-1 gap-3 pt-2 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>API client ID</Label>
                <Input
                  autoComplete="off"
                  spellCheck={false}
                  value={conn.apiKey ?? ''}
                  onChange={(e) => onChange({ apiKey: e.target.value })}
                  placeholder="PropSpace API client ID"
                />
              </div>
              <div className="space-y-1.5">
                <Label>API client secret</Label>
                <Input
                  type="password"
                  autoComplete="new-password"
                  spellCheck={false}
                  value={conn.apiSecret ?? ''}
                  onChange={(e) => onChange({ apiSecret: e.target.value })}
                  placeholder="Paired with the client ID"
                />
              </div>
              <p className="font-mono text-[10px] text-muted-foreground sm:col-span-2">
                With the API login, listings, agents and leads go through
                PropSpace's API and the feed URL only adds amenities. Without
                it, listings sync from the feed and leads aren't sent.
              </p>
            </div>
          )}
          {conn.type === 'goyzer' && (
            <GoyzerLeadFields
              value={conn.goyzer ?? {}}
              onChange={(goyzer) => onChange({ goyzer })}
            />
          )}
          {conn.type === 'propfusion' && (
            <div className="space-y-1.5 pt-2">
              <Label>Lead webhook URL (optional)</Label>
              <Input
                type="url"
                spellCheck={false}
                value={conn.leadUrl ?? ''}
                onChange={(e) => onChange({ leadUrl: e.target.value })}
                placeholder="https://your-company-api.propfusion.io/integration/webhook/…"
              />
              <p className="font-mono text-[10px] text-muted-foreground">
                Only needed to send your leads into Propfusion. Listings sync
                from the feed URL alone; only live listings are published.
              </p>
            </div>
          )}
        </div>
      )}

      {conn.type !== 'other' && (
        <PriceFilterFields
          value={conn.priceFilter ?? {}}
          onChange={(priceFilter) => onChange({ priceFilter })}
        />
      )}

      <SyncStatusStrip status={status} />
    </div>
  );
}

type PriceFilter = NonNullable<CrmConnection['priceFilter']>;

// Blank limits dropped; 'invalid' when a minimum is above its maximum.
function cleanPriceFilter(f: PriceFilter | undefined): PriceFilter | null | 'invalid' {
  if (!f) return null;
  const out: PriceFilter = {};
  for (const k of ['minSale', 'maxSale', 'minRent', 'maxRent'] as const) {
    const v = f[k];
    if (typeof v === 'number' && Number.isFinite(v) && v > 0) out[k] = v;
  }
  if (out.minSale && out.maxSale && out.minSale > out.maxSale) return 'invalid';
  if (out.minRent && out.maxRent && out.minRent > out.maxRent) return 'invalid';
  return Object.keys(out).length > 0 ? out : null;
}

// Optional price limits for this connection (legacy's min/max price).
function PriceFilterFields({
  value,
  onChange,
}: {
  value: PriceFilter;
  onChange: (next: PriceFilter) => void;
}) {
  const field = (key: keyof PriceFilter, label: string) => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input
        type="number"
        inputMode="numeric"
        min={0}
        step={1}
        value={value[key] ?? ''}
        onChange={(e) => {
          const n = e.target.value === '' ? undefined : Number(e.target.value);
          onChange({ ...value, [key]: n != null && Number.isFinite(n) && n > 0 ? n : undefined });
        }}
        placeholder="No limit"
      />
    </div>
  );
  return (
    <div className="mt-4 space-y-3 border-t border-border pt-3 dark:border-white/10">
      <p className="text-xs font-medium">Price filter (optional)</p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {field('minSale', 'Min sale price')}
        {field('maxSale', 'Max sale price')}
        {field('minRent', 'Min rent per year')}
        {field('maxRent', 'Max rent per year')}
      </div>
      <p className="font-mono text-[10px] text-muted-foreground">
        AED. Listings outside these prices aren't published — they show as
        held back in the sync status, with the reason. Monthly rents are
        compared ×12.
      </p>
    </div>
  );
}

// PropData: an API vendor login (support@propdata.net issues them). The
// site ID picks the site when the account reaches several; the branch ID
// is where leads go when the enquired listing has no branch.
function PropDataFields({
  value,
  onChange,
}: {
  value: NonNullable<CrmConnection['propdata']>;
  onChange: (next: NonNullable<CrmConnection['propdata']>) => void;
}) {
  const field = (
    key: 'username' | 'password' | 'siteId' | 'branchId',
    label: string,
    placeholder: string,
    secret = false,
  ) => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input
        type={secret ? 'password' : 'text'}
        autoComplete={secret ? 'new-password' : 'off'}
        spellCheck={false}
        value={value[key] ?? ''}
        onChange={(e) => onChange({ ...value, [key]: e.target.value })}
        placeholder={placeholder}
      />
    </div>
  );
  return (
    <div className="mt-3 space-y-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {field('username', 'API username', 'PropData API vendor username')}
        {field('password', 'API password', 'Paired with the username', true)}
        {field('siteId', 'Site ID (optional)', 'When the account has several sites')}
        {field('branchId', 'Default branch ID (optional)', 'Where leads go without a listing branch')}
        <div className="space-y-1.5">
          <Label>Environment</Label>
          <Select
            searchable={false}
            options={[
              { value: 'production', label: 'Production' },
              { value: 'staging', label: 'Staging' },
            ]}
            value={value.environment ?? 'production'}
            onChange={(v) =>
              onChange({ ...value, environment: v === 'staging' ? 'staging' : 'production' })
            }
          />
        </div>
      </div>
      <p className="font-mono text-[10px] text-muted-foreground">
        Active listings, the agent roster and leads go through the PropData
        API. Beta: built from PropData's API docs, not yet checked against a
        live account.
      </p>
    </div>
  );
}

// Goyzer's lead push (ContactInsert2): the access + group code are the
// login; the rest are Goyzer lookup IDs, with legacy's defaults when blank.
function GoyzerLeadFields({
  value,
  onChange,
}: {
  value: NonNullable<CrmConnection['goyzer']>;
  onChange: (next: NonNullable<CrmConnection['goyzer']>) => void;
}) {
  const field = (
    key: Exclude<keyof NonNullable<CrmConnection['goyzer']>, 'alwaysFallback'>,
    label: string,
    placeholder: string,
    secret = false,
  ) => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input
        type={secret ? 'password' : 'text'}
        autoComplete={secret ? 'new-password' : 'off'}
        spellCheck={false}
        value={value[key] ?? ''}
        onChange={(e) => onChange({ ...value, [key]: e.target.value })}
        placeholder={placeholder}
      />
    </div>
  );
  return (
    <div className="space-y-3 pt-2">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {field('accessCode', 'Access code (optional)', 'Goyzer API access code', true)}
        {field('groupCode', 'Group code (optional)', 'Goyzer API group code', true)}
        {field('mediaName', 'Media name ID', '204597')}
        {field('methodOfContact', 'Method of contact ID', '204531')}
        {field('fallbackBrokerId', 'Fallback agent ID', '0000')}
      </div>
      <div className="flex items-center justify-between gap-3">
        <Label className="font-normal">Send every lead to the fallback agent</Label>
        <Switch
          checked={value.alwaysFallback === true}
          onCheckedChange={(on) => onChange({ ...value, alwaysFallback: on || undefined })}
          aria-label="Send every lead to the fallback agent"
        />
      </div>
      <p className="font-mono text-[10px] text-muted-foreground">
        Only needed to send your leads into Goyzer — each lead goes to the
        listing's agent, or the fallback agent (always, when switched on).
        Listings sync from the feed URL alone.
      </p>
    </div>
  );
}

// Last-pull summary for one connection: when it ran, what came in, and
// (expandable) why listings were held back. Reasons come straight from
// the sync log's per-listing entries, so the counts always add up to
// the "held back" figure.
function SyncStatusStrip({ status }: { status: CrmSyncStatus | null }) {
  const [open, setOpen] = React.useState(false);

  if (!status) {
    return (
      <div className="mt-4 rounded-lg border border-dashed border-border px-4 py-3">
        <p className="font-mono text-[11px] text-muted-foreground">
          Not pulled yet — listings and agents appear after the first sync.
        </p>
      </div>
    );
  }

  const { counts, reasons } = status;
  const heldBack = counts.notSupported;
  const when = new Date(status.lastPullAt).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  const failed = Boolean(status.error) || counts.errors > 0;

  return (
    <div className="mt-4 rounded-lg border border-border bg-muted/20 p-4 dark:border-white/10">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="eyebrow">Last pull</div>
          <div className="mt-1 font-sans text-[13px] font-medium text-foreground">
            {when}
          </div>
          <div className="mt-0.5 font-mono text-[10px] text-muted-foreground">
            {status.trigger === 'manual' ? 'Manual' : 'Scheduled'}
            {status.durationMs > 0 &&
              ` · ${Math.round(status.durationMs / 1000)}s`}
          </div>
        </div>
        <span
          className={cn(
            'mono-label inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2 py-0.5',
            failed
              ? 'border-destructive/40 bg-destructive/10 text-destructive'
              : 'border-emerald-200 bg-emerald-50 text-emerald-700',
          )}
        >
          <span
            className={cn(
              'h-1.5 w-1.5 rounded-full',
              failed ? 'bg-destructive' : 'bg-emerald-500',
            )}
          />
          {failed ? 'Failed' : 'Synced'}
        </span>
      </div>

      {status.error && (
        <div className="mt-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 font-mono text-[11px] text-destructive">
          {status.error}
        </div>
      )}

      <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <MetricTile label="New" value={counts.new} tone="success" />
        <MetricTile label="Updated" value={counts.updated} />
        <MetricTile label="Removed" value={counts.removed} />
        <MetricTile label="Held back" value={heldBack} tone="warn" />
      </div>

      {reasons.length > 0 && (
        <>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground shadow-sm transition-colors hover:text-foreground"
          >
            <ChevronDown
              className={cn(
                'h-3.5 w-3.5 transition-transform',
                open && 'rotate-180',
              )}
            />
            {open ? 'Hide reasons' : `Why ${heldBack} held back`}
          </button>
          {open && (
            <div className="mt-2 divide-y divide-[hsl(var(--hairline))] overflow-hidden rounded-md border border-border bg-card">
              {reasons.map((r) => (
                <div
                  key={r.reason}
                  className="flex items-center gap-3 px-3 py-2"
                >
                  <span className="min-w-0 flex-1 font-sans text-[12px] text-foreground">
                    {r.reason.charAt(0).toUpperCase() + r.reason.slice(1)}
                  </span>
                  <span className="mono-label shrink-0 rounded-full border border-border bg-muted px-2 py-0.5 tabular-nums text-muted-foreground">
                    {r.count}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// One compact number tile — same idiom as the dashboard stat cards.
function MetricTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'success' | 'warn';
}) {
  const zero = value === 0;
  return (
    <div className="rounded-md border border-border bg-card px-3 py-2.5 dark:border-white/10">
      <div
        className={cn(
          'numeral font-display text-[20px] font-medium leading-none',
          zero
            ? 'text-muted-foreground'
            : tone === 'success'
              ? 'text-success'
              : tone === 'warn'
                ? 'text-warning'
                : 'text-foreground',
        )}
      >
        {value.toLocaleString()}
      </div>
      <div className="mono-label mt-1.5 text-muted-foreground">{label}</div>
    </div>
  );
}

// ====================================================================
// Lead management
// ====================================================================

// Example of the JSON we POST to a custom endpoint on each new lead.
// Shown read-only so the integrator knows the shape to expect.
// `lead.qualified`, not `lead.created`: the lead is written when the
// form is submitted but held back until the visitor confirms, and only
// qualified leads are ever POSTed. Calling it `created` would misdate
// every webhook by however long they took to confirm.
const ENDPOINT_PAYLOAD = `{
  "event": "lead.qualified",
  "lead": {
    "id": "6a7e1e985e85d4bc57220357",
    "name": "Jane Doe",
    "email": "jane@example.com",
    "phone": "+971 50 123 4567",
    "qualification": "qualified",
    "verifiedOver": "email",
    "answers": [
      { "question": "What's your budget?", "answer": "1–2M AED" }
    ]
  },
  "listing": {
    "externalId": "DP-S-12345",
    "title": "2BR Apartment · Dubai Marina",
    "price": 2100000,
    "currency": "AED",
    "area": "Dubai Marina",
    "city": "Dubai"
  },
  "proptree": { "alias": "luxevista" },
  "createdAt": "2026-05-29T12:34:56.000Z"
}`;

// Where captured leads get routed. Independent switches (any combination
// can be on), backed by enterprise.leadRouting via /api/enterprise.
function LeadManagementSection({
  hasCrm,
  refreshKey,
}: {
  hasCrm: boolean | null;
  refreshKey: number;
}) {
  const [lead, setLead] = React.useState<LeadRouting | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [testing, setTesting] = React.useState(false);
  const [testResult, setTestResult] = React.useState<{
    ok: boolean;
    status: number | null;
    statusText: string | null;
    durationMs: number;
  } | null>(null);

  // Tests the URL as typed, not as saved — the point is to find out it's
  // wrong before saving something that silently drops leads.
  const testEndpoint = async (url: string) => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetch('/api/enterprise/lead-routing/test-endpoint', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ url: url.trim() }),
      });
      const body = (await res.json()) as {
        ok?: boolean;
        status?: number | null;
        statusText?: string | null;
        durationMs?: number;
        message?: string;
      };
      if (!res.ok) {
        // A 400 from us (bad URL) is still a result worth showing in the
        // same place as a result from their server.
        setTestResult({
          ok: false,
          status: null,
          statusText: body.message ?? 'Request rejected',
          durationMs: body.durationMs ?? 0,
        });
        return;
      }
      setTestResult({
        ok: body.ok === true,
        status: body.status ?? null,
        statusText: body.statusText ?? null,
        durationMs: body.durationMs ?? 0,
      });
    } catch (err) {
      setTestResult({
        ok: false,
        status: null,
        statusText: (err as Error).message,
        durationMs: 0,
      });
    } finally {
      setTesting(false);
    }
  };

  React.useEffect(() => {
    let alive = true;
    client.enterprise
      .getLeadRouting()
      .then((r) => {
        if (!alive) return;
        setLead(r);
        setLoading(false);
      })
      .catch((err: Error) => {
        if (!alive) return;
        setLoadError(err.message || "Couldn't load lead settings.");
        setLoading(false);
      });
    return () => {
      alive = false;
    };
    // refreshKey: connections saves auto-flip leadRouting.toCrm on the
    // server, so refetch to show the current truth.
  }, [refreshKey]);

  async function handleSave() {
    if (!lead || saving) return;
    setError(null);
    setSaving(true);
    try {
      const next = await client.enterprise.updateLeadRouting(lead);
      setLead(next);
      setSaved(true);
    } catch (err) {
      setError((err as Error).message || 'Could not save. Try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="p-6">
      <div className="font-sans text-[15px] font-semibold text-foreground">
        Lead management
      </div>
      <p className="mt-1 font-mono text-[11px] text-muted-foreground">
        Where captured leads are sent. Turn on any combination.
      </p>

      {loading ? (
        <div className="mt-5 space-y-4">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-10 w-full animate-pulse rounded-md bg-muted"
            />
          ))}
        </div>
      ) : loadError ? (
        <div className="mt-5 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 font-mono text-[12px] text-destructive">
          {loadError}
        </div>
      ) : lead ? (
        <>
          <div className="mt-4 divide-y divide-[hsl(var(--hairline))]">
            <LeadRow
              title="Send leads to CRM"
              subtitle={
                hasCrm
                  ? 'Each captured lead is pushed into your connected CRM.'
                  : 'Connect a CRM first — this turns on automatically when you do.'
              }
              checked={lead.toCrm}
              disabled={!hasCrm}
              onCheckedChange={(v) => {
                setSaved(false);
                setLead((p) => (p ? { ...p, toCrm: v } : p));
              }}
            />

            <LeadRow
              title="Send leads to agents' WhatsApp"
              subtitle="Each lead goes to the WhatsApp number of the agent who owns the listing or proptree it came from."
              checked={lead.toAgentWhatsapp}
              onCheckedChange={(v) => {
                setSaved(false);
                setLead((p) => (p ? { ...p, toAgentWhatsapp: v } : p));
              }}
            />

            <LeadRow
              title="Send leads to a central WhatsApp"
              subtitle="One brokerage-wide WhatsApp number."
              checked={lead.centralWhatsapp.enabled}
              onCheckedChange={(v) => {
                setSaved(false);
                setLead((p) =>
                  p
                    ? {
                        ...p,
                        centralWhatsapp: { ...p.centralWhatsapp, enabled: v },
                      }
                    : p,
                );
              }}
            >
              {lead.centralWhatsapp.enabled && (
                <div className="mt-3">
                  <Label htmlFor="lead-wa">WhatsApp number</Label>
                  <Input
                    id="lead-wa"
                    className="mt-1"
                    value={lead.centralWhatsapp.number}
                    onChange={(e) => {
                      const number = e.target.value;
                      setSaved(false);
                      setLead((p) =>
                        p
                          ? {
                              ...p,
                              centralWhatsapp: {
                                ...p.centralWhatsapp,
                                number,
                              },
                            }
                          : p,
                      );
                    }}
                    placeholder="+971 50 123 4567"
                  />
                </div>
              )}
            </LeadRow>

            <LeadRow
              title="Send leads to a central email"
              subtitle="One inbox for the whole brokerage."
              checked={lead.centralEmail.enabled}
              onCheckedChange={(v) => {
                setSaved(false);
                setLead((p) =>
                  p
                    ? { ...p, centralEmail: { ...p.centralEmail, enabled: v } }
                    : p,
                );
              }}
            >
              {lead.centralEmail.enabled && (
                <div className="mt-3">
                  <Label htmlFor="lead-email">Email address</Label>
                  <Input
                    id="lead-email"
                    type="email"
                    className="mt-1"
                    value={lead.centralEmail.email}
                    onChange={(e) => {
                      const email = e.target.value;
                      setSaved(false);
                      setLead((p) =>
                        p
                          ? { ...p, centralEmail: { ...p.centralEmail, email } }
                          : p,
                      );
                    }}
                    placeholder="leads@yourbrokerage.com"
                  />
                </div>
              )}
            </LeadRow>

            <LeadRow
              title="Send leads to an endpoint"
              subtitle="POST each lead to a webhook (Zapier, your CRM, an in-house service)."
              checked={lead.endpoint.enabled}
              onCheckedChange={(v) => {
                setSaved(false);
                setLead((p) =>
                  p ? { ...p, endpoint: { ...p.endpoint, enabled: v } } : p,
                );
              }}
            >
              {lead.endpoint.enabled && (
                <div className="mt-3 space-y-3">
                  <div>
                    <Label htmlFor="lead-endpoint">Endpoint URL</Label>
                    <Input
                      id="lead-endpoint"
                      type="url"
                      className="mt-1"
                      value={lead.endpoint.url}
                      onChange={(e) => {
                        const url = e.target.value;
                        setSaved(false);
                        setLead((p) =>
                          p ? { ...p, endpoint: { ...p.endpoint, url } } : p,
                        );
                      }}
                      placeholder="https://hooks.zapier.com/hooks/catch/…"
                    />
                  </div>
                  <div>
                    <div className="eyebrow mb-1.5">Payload format</div>
                    <pre className="overflow-x-auto rounded-md border border-border bg-muted/40 p-3 font-mono text-[11px] leading-relaxed text-foreground/80">
                      {ENDPOINT_PAYLOAD}
                    </pre>
                    <p className="mt-1.5 font-mono text-[10px] text-muted-foreground">
                      Sent as a JSON POST (content-type application/json)
                      when a lead becomes qualified. Unqualified leads are
                      never sent.
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={!lead.endpoint.url.trim() || testing}
                        onClick={() => void testEndpoint(lead.endpoint.url)}
                      >
                        {testing ? 'Sending…' : 'Test webhook'}
                      </Button>
                      {testResult && (
                        // The raw status is the point — an integrator
                        // debugging their own handler needs the number,
                        // not our interpretation of it.
                        <span
                          className={cn(
                            'font-mono text-[11px]',
                            testResult.ok ? 'text-success' : 'text-destructive',
                          )}
                        >
                          {testResult.status
                            ? `${testResult.status} ${testResult.statusText ?? ''}`.trim()
                            : (testResult.statusText ?? 'no response')}
                          <span className="text-muted-foreground">
                            {' '}· {testResult.durationMs}ms
                          </span>
                        </span>
                      )}
                    </div>
                    <p className="mt-1.5 font-mono text-[10px] text-muted-foreground">
                      Sends a sample payload with{' '}
                      <span className="text-foreground">x-proptr-test: 1</span>{' '}
                      so your handler can ignore it.
                    </p>
                  </div>
                </div>
              )}
            </LeadRow>
          </div>

          {error && (
            <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 font-mono text-[12px] text-destructive">
              {error}
            </div>
          )}

          <div className="mt-5 flex items-center justify-end gap-3">
            {saved && !saving && (
              <span className="inline-flex items-center gap-1.5 font-mono text-[11px] text-success">
                <Check className="h-3.5 w-3.5" />
                Saved
              </span>
            )}
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="animate-spin" /> : <Check />}
              {saving ? 'Saving…' : 'Save lead settings'}
            </Button>
          </div>
        </>
      ) : null}
    </Card>
  );
}

function LeadRow({
  title,
  subtitle,
  checked,
  onCheckedChange,
  disabled = false,
  children,
}: {
  title: string;
  subtitle: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div className={cn('py-4 first:pt-0 last:pb-0', disabled && 'opacity-60')}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="font-sans text-[13px] font-medium text-foreground">
            {title}
          </div>
          <p className="mt-0.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
            {subtitle}
          </p>
        </div>
        <Switch
          checked={checked}
          onCheckedChange={onCheckedChange}
          disabled={disabled}
          aria-label={title}
        />
      </div>
      {children}
    </div>
  );
}
